import { useEffect, useMemo, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { dateIt, eur } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/app/StatusBadge";
import ProfiledCell from "@/components/app/ProfiledCell";
import { toast } from "sonner";
import { Download, Trash2, Search, Eye, FileSpreadsheet, FileText, GraduationCap } from "lucide-react";

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-400">—</span>}</div>
  </div>
);

export default function AdminEnrollments() {
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [pid, setPid] = useState("all");
  const [cid, setCid] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (pid !== "all") params.partner_id = pid;
    if (cid !== "all") params.course_id = cid;
    if (status !== "all") params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/enrollments", { params }).then((r) => setItems(r.data));
  }, [pid, cid, status, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/courses").then((r) => setCourses(r.data));
  }, []);

  const del = async (id) => {
    if (!window.confirm("Confermi l'eliminazione dell'iscritto? L'azione sarà registrata nei log.")) return;
    try {
      await api.delete(`/enrollments/${id}`);
      toast.success("Iscritto eliminato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const toggleProfiled = async (e) => {
    try {
      await api.post(`/enrollments/${e.id}/profiled`, { value: !e.profiled });
      toast.success(e.profiled ? "Profilazione rimossa" : `${e.first_name} ${e.last_name} segnato come Profilato`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento"); }
  };

  const filtered = useMemo(() => items.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return `${e.first_name} ${e.last_name} ${e.email} ${e.company} ${e.fiscal_code} ${e.order_number || ""} ${e.professional_profile || ""} ${e.professional_province || ""}`
      .toLowerCase().includes(s);
  }), [items, q]);
  const totalSaving = useMemo(() => filtered.reduce((s, e) => s + Number(e.discount_value || 0), 0), [filtered]);

  const exportHref = useCallback((ext) => {
    const url = new URL(`${API}/export/enrollments${ext === "xlsx" ? ".xlsx" : ""}`, window.location.origin);
    if (pid !== "all") url.searchParams.set("partner_id", pid);
    if (cid !== "all") url.searchParams.set("course_id", cid);
    if (status !== "all") url.searchParams.set("status", status);
    if (dateFrom) url.searchParams.set("date_from", dateFrom);
    if (dateTo) url.searchParams.set("date_to", dateTo);
    return url.toString();
  }, [pid, cid, status, dateFrom, dateTo]);

  return (
    <div className="space-y-6" data-testid="admin-enrollments">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Iscrizioni corsisti</h1>
          <p className="text-slate-500 mt-1">Visualizza tutti i dati inseriti dal partner · Esporta in CSV o Excel.</p>
        </div>
        <div className="flex gap-2">
          <a href={exportHref("csv")} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="export-enrollments-csv"><FileText className="w-4 h-4 mr-1" /> Esporta CSV</Button>
          </a>
          <a href={exportHref("xlsx")} target="_blank" rel="noopener noreferrer">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-enrollments-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Esporta Excel
            </Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca per nome, email, CF, ordine, profilo…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="enroll-search" />
        </div>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger data-testid="enroll-partner-filter"><SelectValue placeholder="Partner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i partner</SelectItem>
            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="enroll-course-filter"><SelectValue placeholder="Corso" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i corsi</SelectItem>
            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="enroll-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="iscritto">Iscritto</SelectItem>
            <SelectItem value="confermato">Confermato</SelectItem>
            <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
          </SelectContent>
        </Select>
        <div><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Dal" data-testid="enroll-date-from" /></div>
        <div><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Al" data-testid="enroll-date-to" /></div>
      </div>

      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="enroll-saving-card">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{filtered.length}</span> iscrizioni nel periodo selezionato</div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Totale sconto concesso ai partner</div>
            <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="enroll-saving-total">{eur(totalSaving)}</div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Numero Ordine</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Corsista</TableHead>
              <TableHead>Corso</TableHead>
              <TableHead>Prezzo Partner</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Profilato</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((e) => (
              <TableRow key={e.id} data-testid={`admin-enroll-${e.id}`}>
                <TableCell className="text-xs text-slate-600 whitespace-nowrap" data-testid={`enroll-date-${e.id}`}>{dateIt(e.created_at)}</TableCell>
                <TableCell className="font-mono text-xs text-slate-600 whitespace-nowrap" data-testid={`enroll-order-${e.id}`}>{e.order_number || "—"}</TableCell>
                <TableCell data-testid={`enroll-partner-${e.id}`}>{e.partner_name}</TableCell>
                <TableCell className="font-medium whitespace-nowrap" data-testid={`enroll-name-${e.id}`}>{e.first_name} {e.last_name}</TableCell>
                <TableCell data-testid={`enroll-course-${e.id}`}>{e.course_title}</TableCell>
                <TableCell className="font-mono text-xs whitespace-nowrap font-semibold" data-testid={`enroll-applied-${e.id}`}>{e.applied_price != null ? eur(e.applied_price) : "—"}</TableCell>
                <TableCell><StatusBadge status={e.status} /></TableCell>
                <TableCell><ProfiledCell item={e} onToggle={toggleProfiled} testKey={e.id} disabled={e.status === "cancelled"} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => setDetail(e)} data-testid={`view-enroll-${e.id}`} aria-label="Dettagli iscritto">
                    <Eye className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => del(e.id)} data-testid={`delete-enroll-${e.id}`} aria-label="Elimina">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">Nessuna iscrizione.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enroll-detail-dialog">
          <DialogHeader>
            <DialogTitle>Scheda iscritto · {detail?.first_name} {detail?.last_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Numero ordine" value={detail.order_number} mono />
                <Field label="Partner" value={detail.partner_name} />
                <Field label="Corso" value={detail.course_title} />
                <Field label="Stato" value={detail.status} />
                <Field label="Nome" value={detail.first_name} />
                <Field label="Cognome" value={detail.last_name} />
                <Field label="Codice fiscale" value={detail.fiscal_code} mono />
                <Field label="Data di nascita" value={detail.birth_date ? dateIt(detail.birth_date) : ""} />
                <Field label="Luogo di nascita" value={detail.birth_place} />
                <Field label="Email" value={detail.email} />
                <Field label="Telefono" value={detail.phone} />
                <Field label="Azienda" value={detail.company} />
                <Field label="Mansione" value={detail.role} />
                <Field label="Data iscrizione" value={dateIt(detail.created_at)} />
                <Field label="Prezzo standard" value={detail.standard_price != null ? eur(detail.standard_price) : ""} />
                <Field label="Prezzo partner applicato" value={detail.applied_price != null ? eur(detail.applied_price) : ""} />
                <Field label="Sconto concesso" value={detail.discount_value != null ? eur(detail.discount_value) : ""} />
              </div>
              <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/60">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-4 h-4 text-amber-600" />
                  <div className="text-sm font-bold text-slate-800">Crediti Formativi Professionali</div>
                  {detail.requires_cfp
                    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Richiesti</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Non richiesti</Badge>}
                </div>
                {detail.requires_cfp && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Profilo professionale" value={detail.professional_profile} />
                    <Field label="Provincia dell'ordine" value={detail.professional_province} />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <a href={`${API}/export/enrollments.xlsx?partner_id=${detail.partner_id || "all"}&course_id=${detail.course_id || "all"}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline"><Download className="w-4 h-4 mr-1" /> Esporta scheda partner</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
